import { jwtDecode } from "jwt-decode";
import type { UserResponse } from "../api/generated/types.gen";
import { TOKEN_KEYS, DecodedToken } from "../api/types";

// Re-export for convenience
export { TOKEN_KEYS };
export type { DecodedToken };

/**
 * Decode JWT and extract user information
 *
 * The JWT contains essential user info (id, email, role) for quick access without API calls.
 * For complete user data including full_name and timestamps, use getCurrentUserInfoApiAuthMeGet().
 *
 * @returns Decoded token with user info (id, email, role) and expiration time
 */
export function decodeToken(token: string): {
  user: Pick<UserResponse, "id" | "email" | "role">;
  exp: number;
} {
  const decoded = jwtDecode<DecodedToken>(token);
  return {
    user: {
      id: decoded.sub,
      email: decoded.email,
      role: decoded.role as "admin" | "client",
    },
    exp: decoded.exp,
  };
}

/**
 * Store tokens in localStorage
 */
export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
  localStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken);
}

/**
 * Retrieve tokens from localStorage
 */
export function getTokens(): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  return {
    accessToken: localStorage.getItem(TOKEN_KEYS.ACCESS),
    refreshToken: localStorage.getItem(TOKEN_KEYS.REFRESH),
  };
}

/**
 * Clear all auth tokens from localStorage
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEYS.ACCESS);
  localStorage.removeItem(TOKEN_KEYS.REFRESH);
}

/**
 * Calculate milliseconds until token expires (with 1 min buffer)
 */
export function getRefreshDelay(exp: number): number {
  const expirationTime = exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 60000; // 1 minute
  const delay = expirationTime - currentTime - bufferTime;
  return Math.max(0, delay);
}
